import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { getCompanyId } from "@/common/services/company.api";
import { getLoans } from "@/common/services/loans.api";
import { useCompanyStore } from "@/common/states/store/Company";
import { ILoan, LOAN_STATUS } from "@/types/loan.type";
import { Suspense, useEffect, useState } from "react";
import { ModalCreateLoan } from "../modals/ModalCreateLoan";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { useNavigate } from "react-router-dom";
import { createClickableLink } from "../helpers/clickOptionsTable";
import { getCompanySigla } from "@/common/helpers/company.helpers";
const STATUS_LABELS = {
  [LOAN_STATUS.PENDING]: "Pendente",
  [LOAN_STATUS.UNDER_ANALYSIS]: "Aguardando Análise",
  [LOAN_STATUS.APPROVED]: "Aprovado",
  [LOAN_STATUS.REPROVED]: "Rejeitado",
  [LOAN_STATUS.CANCELED]: "Cancelado",
  [LOAN_STATUS.UNDEFINED]: "Processado",
};

const STATUS_COLORS = {
  [LOAN_STATUS.PENDING]: "bg-bluePrimary text-white",
  [LOAN_STATUS.UNDER_ANALYSIS]: "bg-yellowPrimary",
  [LOAN_STATUS.APPROVED]: "bg-greenPrimary text-white",
  [LOAN_STATUS.REPROVED]: "bg-yellowPrimary",
  [LOAN_STATUS.CANCELED]: "bg-redPrimary text-white",
  [LOAN_STATUS.UNDEFINED]: "bg-grayPrimary",
};

function getStatusLabels(status: LOAN_STATUS) {
  return (
    <span
      className={`inline-block px-2 py-1 rounded-md text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] md:max-w-full ${STATUS_COLORS[status]}`}
      title={STATUS_LABELS[status] || status}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  companyId: "Cidade",
  createdAt: "Data de Criação",
  renterName: "Nome do Locatário",
  whatsapp: "WhatsApp",
  value: "Valor Solicitado",
  status: "Status",
};

function TableLoans() {
  const companyId = getCompanyId();
  const company = useCompanyStore();
  const { notifyError } = useToastContext();
  const navigate = useNavigate();
  const [loanData, setLoanData] = useState<ILoan[]>([]);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    situation: "",
    orderBy: "-createdAt",
  });

  useEffect(() => {
    async function loadRenterLoan(filters, page) {
      setIsLoading(true);
      setLoanData([]);

      const result = await getLoans({ ...filters, page });

      const mappedResult = (Array.isArray(result.data) ? result.data : []).map(
        (item) => ({
          ...item,
          renterName: item?.renterInfo?.renterName ?? "-",
          whatsapp: item?.renterInfo?.whatsapp ?? "-",
          value: item?.paymentInfo?.totalAmountPending ?? 0,
        })
      );

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }
      setLoanData(mappedResult);
      setIsLoading(false);
    }
    loadRenterLoan(filters, page);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, companyId, filters, page]);

  const actions = [
    {
      label: "Editar",
      action: (data) =>
        setAction({ eventName: "editMotoClub", data: (data as ILoan)._id }),
      permission: "edit:loan",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Tabela de Empréstimos"
          placeholder="Digite o nome, o e-mail, o CPF ou WhatsApp."
          newItemLabel="Novo Empréstimo"
          onNewItemClick={() =>
            setAction({ eventName: "createLoan", data: undefined })
          }
          statusOptions={{}}
          onSearch={handleFilterChange}
          entity="loan"
          tableData={loanData}
        />

        <GenericTable
          headers={headers}
          data={loanData}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId);
              },
              renterName: (name, loanData) =>
                createClickableLink(name as string, loanData, navigate),

              createdAt: (date, loanData) =>
                createClickableLink(
                  formatDateAndTime(date),
                  loanData,
                  navigate
                ),

              whatsapp: (whatsapp, loanData) =>
                createClickableLink(whatsapp as string, loanData, navigate),

              value: (value, loanData) =>
                createClickableLink(formatCurrency(value), loanData, navigate),

              status: (status, loanData) =>
                createClickableLink(
                  getStatusLabels(status),
                  loanData,
                  navigate,
                  "cursor-pointer" // className diferente para status
                ),

              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="loan"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("createLoan") && (
          <ModalCreateLoan onClose={cleanActionData} />
        )}
      </div>
    </Suspense>
  );
}

export default TableLoans;
